/**
 * MesCampagnesPage — /mes-campagnes (Sprint 4)
 * Dashboard athlète : liste + brouillon/soumission.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Shirt, PlusCircle, Send, Edit3, Trophy, Info } from "lucide-react";

const STATUS_LABEL = {
  draft: { label: "Brouillon", color: "bg-secondary text-foreground" },
  submitted: { label: "Envoyée à AITO", color: "bg-amber-100 text-amber-800" },
  admin_review: { label: "En cours d'étude", color: "bg-amber-100 text-amber-800" },
  approved: { label: "Approuvée", color: "bg-emerald-100 text-emerald-800" },
  preorder_open: { label: "Précommande ouverte", color: "bg-[hsl(var(--aito-lagoon))]/20 text-foreground" },
  threshold_reached: { label: "Seuil atteint", color: "bg-emerald-200 text-emerald-900" },
  confirmed_offline: { label: "Confirmée hors plateforme", color: "bg-emerald-200 text-emerald-900" },
  fulfilled: { label: "Terminée", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Annulée", color: "bg-muted text-muted-foreground" },
};

export default function MesCampagnesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", objective: "", competition_or_event: "", description: "", reason_of_support: "", product_type: "tshirt", deadline: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns/mine");
      setItems(data.items || []);
      setAthlete(data.athlete || null);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Un titre est requis"); return; }
    setBusy(true);
    try {
      const payload = { ...form, deadline: form.deadline ? new Date(form.deadline).toISOString() : null };
      await api.post("/campaigns/mine", payload);
      toast.success("Brouillon créé.");
      setOpen(false);
      setForm({ title: "", objective: "", competition_or_event: "", description: "", reason_of_support: "", product_type: "tshirt", deadline: "" });
      load();
    } catch (e) { showError(e, { prefix: "Créer :" }); }
    finally { setBusy(false); }
  };

  const submit = async (id) => {
    if (!window.confirm("Envoyer cette demande à AITO.PF ?")) return;
    try {
      await api.post(`/campaigns/mine/${id}/submit`);
      toast.success("Demande envoyée. AITO.PF va l'examiner.");
      load();
    } catch (e) { showError(e); }
  };

  if (!user) return <div className="p-6"><Card className="p-6 text-center">Connectez-vous.</Card></div>;

  if (!loading && !athlete) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="mes-campagnes-no-athlete">
        <Card className="p-6 text-center">
          <Shirt className="mx-auto h-10 w-10 text-muted-foreground mb-2" />
          <h1 className="text-lg font-semibold">Aucune fiche athlète associée</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Seul un athlète propriétaire de sa fiche peut proposer une campagne AITO x moi. Vous pouvez revendiquer votre fiche existante ou contacter AITO.PF.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6" data-testid="mes-campagnes-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Shirt className="h-6 w-6 text-[hsl(var(--aito-gold))]" />Mes campagnes
          </h1>
          <p className="text-xs text-muted-foreground">Proposez un partenariat AITO.PF x {athlete?.full_name}.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="mes-campagnes-create" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
          <PlusCircle className="h-4 w-4 mr-1.5" />Proposer une campagne
        </Button>
      </div>

      <Card className="p-3 mb-3 border-l-4 border-[hsl(var(--aito-lagoon))] bg-secondary/40 text-xs">
        <div className="flex gap-2 items-start"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Comment ça se passe ?</strong> Vous décrivez votre projet sportif et l'idée de produit. AITO.PF valide, définit le produit, le prix et ouvre les précommandes par tranches de 20 unités. Les paiements et livraisons sont organisés hors plateforme.
          </div>
        </div>
      </Card>

      <div className="grid gap-3">
        {items.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">Aucune campagne pour le moment.</Card>
        ) : items.map((c) => {
          const st = STATUS_LABEL[c.status] || { label: c.status, color: "bg-muted" };
          const currentT = (c.tranches || []).find((t) => t.tranche_number === c.current_tranche_number);
          return (
            <Card key={c.id} className="p-4" data-testid={`mes-campagnes-item-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg leading-tight">{c.title}</div>
                  {c.objective && <div className="text-xs text-muted-foreground mt-0.5"><Trophy className="inline h-3 w-3 mr-1" />{c.objective}</div>}
                  {currentT && (
                    <div className="text-xs mt-1">Tranche {currentT.tranche_number} — <strong>{currentT.confirmed_count || 0}/{currentT.batch_size || 20}</strong> confirmées</div>
                  )}
                </div>
                <Badge className={st.color}>{st.label}</Badge>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {c.status === "draft" && (
                  <Button size="sm" onClick={() => submit(c.id)} data-testid={`mes-campagnes-submit-${c.id}`}><Send className="h-3.5 w-3.5 mr-1" />Envoyer à AITO</Button>
                )}
                {["preorder_open", "threshold_reached", "confirmed_offline", "fulfilled"].includes(c.status) && (
                  <Link to={`/soutien/campagnes/${c.slug}`}><Button size="sm" variant="outline">Voir page publique</Button></Link>
                )}
                <div className="ml-auto text-[10px] text-muted-foreground">Créé {c.created_at?.slice(0, 10)}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mes-campagnes-dialog">
          <DialogHeader>
            <DialogTitle>Proposer une campagne AITO x moi</DialogTitle>
            <DialogDescription>Décrivez votre projet. AITO.PF étudiera votre demande, préparera le produit et ouvrira les précommandes si validation.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : T-shirt JP 2027 — Va'a" data-testid="dialog-title" /></div>
            <div><Label>Objectif sportif</Label><Input value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Ex : Coupe du Monde 2027" data-testid="dialog-objective" /></div>
            <div><Label>Compétition / événement</Label><Input value={form.competition_or_event} onChange={(e) => setForm({ ...form, competition_or_event: e.target.value })} data-testid="dialog-competition" /></div>
            <div><Label>Description (visible publiquement après validation)</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="dialog-description" /></div>
            <div><Label>Motif du soutien (privé — visible AITO uniquement)</Label><Textarea rows={2} value={form.reason_of_support} onChange={(e) => setForm({ ...form, reason_of_support: e.target.value })} data-testid="dialog-reason" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Type de produit souhaité</Label><Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} placeholder="tshirt, hoodie, casquette…" data-testid="dialog-product" /></div>
              <div><Label>Échéance</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="dialog-deadline" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={create} disabled={busy || !form.title.trim()} data-testid="dialog-create">{busy ? "Création…" : "Créer le brouillon"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
